import React, { useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { buildUserGuidePdfUrl, APP_REVISION } from '../lib/appRevision';

/**
 * QR-code card for the User Guide page.
 *
 * Displays a scannable QR pointing to the public User Guide PDF endpoint.
 * "Copy image" places a PNG on the clipboard so the accountant can paste
 * it into an email / chat / Word doc. "Download PNG" saves it to disk.
 * A hidden Canvas is what we serialise for those actions — the visible
 * markup uses the SVG version so it prints crisply at any size.
 */
export default function UserGuideQRCard({ note, targetUrl }) {
  const url = targetUrl || buildUserGuidePdfUrl();
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const getCanvasEl = () =>
    canvasRef.current?.querySelector('canvas') || canvasRef.current;

  const handleCopyImage = async () => {
    try {
      const canvas = getCanvasEl();
      if (!canvas || typeof canvas.toBlob !== 'function') {
        throw new Error('QR canvas not ready');
      }
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('Could not serialise QR image');
      // Modern browsers → Clipboard API
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({ [blob.type]: blob }),
          ]);
          setCopied(true);
          toast.success('QR code copied to clipboard — paste it anywhere');
          setTimeout(() => setCopied(false), 2000);
          return;
        } catch (writeErr) {
          // Permission denied / insecure context → fall through to URL copy
        }
      }
      // Fallback → copy the URL text so the user has SOMETHING to paste
      await navigator.clipboard.writeText(url);
      toast.success('Clipboard image not available here — PDF link copied instead');
    } catch (e) {
      toast.error(e.message || 'Could not copy QR image');
    }
  };

  const handleDownloadImage = () => {
    try {
      const canvas = getCanvasEl();
      if (!canvas) throw new Error('QR canvas not ready');
      const link = document.createElement('a');
      link.download = `bizcore-user-guide-qr-${APP_REVISION.revisionCode}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR code image downloaded');
    } catch (e) {
      toast.error(e.message || 'Could not download QR image');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('User Guide PDF link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div
      className="rounded-2xl border-2 border-dashed border-[#d4a84b]/40 bg-gradient-to-br from-[#fef8ea] to-white dark:from-[#1e293b] dark:to-[#0f172a] dark:border-[#d4a84b]/30 p-6"
      data-testid="user-guide-qr-card"
    >
      <div className="flex flex-col md:flex-row items-start gap-6">
        {/* Visible SVG QR */}
        <div className="flex-shrink-0 bg-white p-3 rounded-xl shadow-sm ring-1 ring-black/5">
          <QRCodeSVG
            value={url}
            size={168}
            level="M"
            fgColor="#0f172a"
            bgColor="#ffffff"
            includeMargin={false}
            data-testid="user-guide-qr-svg"
          />
        </div>

        {/* Hidden canvas — used for clipboard/download actions */}
        <div className="hidden" ref={canvasRef}>
          <QRCodeCanvas value={url} size={512} level="M" includeMargin fgColor="#0f172a" bgColor="#ffffff" />
        </div>

        <div className="flex-1 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#d4a84b] text-[#0f172a] text-xs font-semibold">
            📱 SCAN &amp; DOWNLOAD
          </div>
          <h3 className="text-lg font-bold text-foreground">
            Scan this QR code to Download PDF of full User guide of Bizcore ERP
          </h3>
          <p className="text-sm text-muted-foreground">
            {note || (
              <>
                Point any smartphone camera at the code — the full user guide PDF
                ({APP_REVISION.revisionCode}) opens instantly. Copy the image and
                paste it into an email, WhatsApp message or printed document.
              </>
            )}
          </p>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={handleCopyImage}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0f172a] text-white text-xs font-medium hover:bg-[#1e293b] dark:bg-[#d4a84b] dark:text-[#0f172a] dark:hover:bg-[#b8912e]"
              data-testid="qr-copy-image-btn"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Copied!' : 'Copy Image'}
            </button>
            <button
              type="button"
              onClick={handleDownloadImage}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#0f172a]/20 text-xs font-medium hover:bg-muted dark:border-[#d4a84b]/40 dark:text-[#d4a84b]"
              data-testid="qr-download-image-btn"
            >
              <Download size={14} />
              Download PNG
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-muted-foreground/20 text-xs font-medium hover:bg-muted"
              data-testid="qr-copy-link-btn"
            >
              <Copy size={14} />
              Copy PDF Link
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-muted-foreground/20 text-xs font-medium hover:bg-muted"
              data-testid="qr-open-pdf-btn"
            >
              <Download size={14} />
              Open PDF
            </a>
          </div>

          <p className="text-[11px] text-muted-foreground pt-2 font-mono break-all">
            {url}
          </p>
        </div>
      </div>
    </div>
  );
}
